/*!
 * BOPF Saved Pieces page hydrator
 *
 * Renders the customer's wishlist on /pages/saved.
 *
 * Data flow:
 *   1. Read localStorage immediately (fast first paint) and render
 *      a placeholder grid with stored prices + "loading" indicators.
 *   2. If we have an email in localStorage, fetch the canonical list
 *      from the Zara backend with LIVE Shopify prices in parallel.
 *   3. Reconcile and re-render with authoritative data.
 *   4. If we have NO localStorage data, show empty state with a soft
 *      "Have you saved on another device?" link to email-recall.
 *
 * Install:
 *   1. Save as: assets/bopf-saved.js
 *   2. Create Shopify page /pages/saved (title: "Saved Pieces").
 *      In the page body in admin, add ONE line:
 *          <div id="bopf-saved-app"></div>
 *   3. Add to layout/theme.liquid before </body>:
 *          {% if page.handle == 'saved' %}
 *            <script src="{{ 'bopf-saved.js' | asset_url }}?v=1" defer></script>
 *          {% endif %}
 */

(function() {
  'use strict';

  var ZARA_BACKEND = 'https://bopf-concierge.onrender.com';
  var STORAGE_EMAIL = 'bopf_zara_email';
  var STORAGE_SAVED = 'bopf_zara_saved';
  var WHATSAPP = '971585669311';

  function log() {
    try { console.log.apply(console, ['[bopf-saved]'].concat([].slice.call(arguments))); } catch(e) {}
  }

  // ── Storage helpers (mirror bopf-wishlist-heart.js) ────────────────
  function getEmail() {
    try { return localStorage.getItem(STORAGE_EMAIL) || ''; } catch(e) { return ''; }
  }
  function setEmail(email) {
    try { localStorage.setItem(STORAGE_EMAIL, email); } catch(e) {}
  }
  function getSavedMap() {
    try {
      var raw = localStorage.getItem(STORAGE_SAVED);
      return raw ? JSON.parse(raw) : {};
    } catch(e) { return {}; }
  }
  function setSavedMap(m) {
    try { localStorage.setItem(STORAGE_SAVED, JSON.stringify(m)); } catch(e) {}
    // Notify the nav heart (and any other listeners) that the wishlist
    // changed. Same custom event the wishlist heart script uses.
    try {
      window.dispatchEvent(new CustomEvent('bopf-wishlist-changed', {
        detail: { count: Object.keys(m).length }
      }));
    } catch(e) {}
  }
  function markUnsavedLocal(handle) {
    var m = getSavedMap();
    delete m[handle];
    setSavedMap(m);
  }

  // ── Number formatting ──────────────────────────────────────────────
  function fmtAed(n) {
    if (n === null || n === undefined || isNaN(n)) return '';
    return 'AED ' + Math.round(parseFloat(n)).toLocaleString();
  }

  function escapeHtml(s) {
    return String(s||'')
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
      .replace(/"/g,'&quot;');
  }

  // ── Style injection ────────────────────────────────────────────────
  function injectStyles() {
    if (document.getElementById('bopf-saved-styles')) return;
    var s = document.createElement('style');
    s.id = 'bopf-saved-styles';
    s.textContent = '' +
      '#bopf-saved-app { font-family: -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif; color: #1F1A17; max-width: 1280px; margin: 0 auto; padding: 40px 24px 80px; }' +

      '.bopf-sp-header { text-align: center; margin-bottom: 48px; }' +
      '.bopf-sp-eyebrow { font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; color: #A0563E; margin: 0 0 12px 0; }' +
      '.bopf-sp-title { font-family: Georgia, "Times New Roman", serif; font-size: 38px; font-weight: 400; color: #1F1A17; margin: 0 0 12px 0; line-height: 1.2; }' +
      '.bopf-sp-title em { font-style: italic; color: #A0563E; position: relative; display: inline-block; }' +
      '.bopf-sp-title em:after { content: ""; position: absolute; left: 0; right: 0; bottom: -6px; height: 8px; ' +
        'background-image: url(\'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 90 6"><path d="M2 3 Q 14 1, 24 3 T 44 3 T 64 3 T 88 3" stroke="%23A0563E" stroke-width="1.4" fill="none" stroke-linecap="round"/></svg>\');' +
        'background-repeat: no-repeat; background-size: 100% 100%; }' +
      '.bopf-sp-subtitle { font-family: Georgia, serif; font-style: italic; font-size: 16px; color: #666; margin: 0; max-width: 480px; margin: 0 auto; line-height: 1.5; }' +

      // Grid
      '.bopf-sp-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 28px 20px; }' +
      '@media (max-width: 600px) { .bopf-sp-grid { grid-template-columns: repeat(2, 1fr); gap: 18px 12px; } #bopf-saved-app { padding: 24px 16px 60px; } .bopf-sp-title { font-size: 26px; } }' +

      // Card
      '.bopf-sp-card { display: flex; flex-direction: column; }' +
      '.bopf-sp-img-wrap { position: relative; aspect-ratio: 4 / 5; background: #f3eee5; overflow: hidden; }' +
      '.bopf-sp-img-wrap a { display: block; width: 100%; height: 100%; }' +
      '.bopf-sp-img-wrap img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.4s ease; }' +
      '.bopf-sp-card:hover .bopf-sp-img-wrap img { transform: scale(1.03); }' +

      '.bopf-sp-remove { position: absolute; top: 10px; right: 10px; width: 30px; height: 30px; ' +
        'background: rgba(255,255,255,0.92); border: none; border-radius: 50%; cursor: pointer; ' +
        'display: flex; align-items: center; justify-content: center; padding: 0; font-size: 18px; color: #1F1A17; ' +
        'transition: background 0.15s, transform 0.1s; z-index: 2; }' +
      '.bopf-sp-remove:hover { background: rgba(255,255,255,1); color: #A0563E; }' +
      '.bopf-sp-remove:active { transform: scale(0.92); }' +

      '.bopf-sp-sold-badge { position: absolute; top: 0; left: 0; right: 0; bottom: 0; ' +
        'background: rgba(250, 246, 240, 0.85); display: flex; flex-direction: column; ' +
        'align-items: center; justify-content: center; pointer-events: none; padding: 16px; }' +
      '.bopf-sp-sold-badge .label { color: #1F1A17; font-family: Georgia, serif; font-style: italic; font-size: 16px; ' +
        'letter-spacing: 0.04em; margin: 0 0 4px 0; text-align: center; }' +
      '.bopf-sp-sold-badge .sub { color: #888; font-size: 11px; letter-spacing: 0.06em; ' +
        'text-transform: uppercase; margin: 0; text-align: center; font-family: -apple-system, sans-serif; }' +

      '.bopf-sp-info { padding: 14px 4px 0 4px; }' +
      '.bopf-sp-info a { text-decoration: none; color: inherit; }' +
      '.bopf-sp-info a:hover .bopf-sp-name { text-decoration: underline; }' +
      '.bopf-sp-name { font-size: 13px; line-height: 1.4; color: #1F1A17; margin: 0 0 6px 0; ' +
        'display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }' +
      '.bopf-sp-price { font-size: 13px; color: #1F1A17; margin: 0; }' +
      '.bopf-sp-price-changed { font-size: 11px; color: #A0563E; font-style: italic; margin-top: 3px; }' +
      '.bopf-sp-price-strike { color: #999; text-decoration: line-through; margin-right: 6px; }' +

      '.bopf-sp-saved-at { font-size: 11px; color: #999; margin-top: 8px; font-style: italic; font-family: Georgia, serif; }' +

      '.bopf-sp-wa-link { display: inline-block; margin-top: 8px; font-size: 11px; color: #A0563E; ' +
        'text-decoration: underline; font-style: italic; font-family: Georgia, serif; cursor: pointer; }' +
      '.bopf-sp-wa-link:hover { color: #8a4732; }' +

      // Empty state
      '.bopf-sp-empty { text-align: center; padding: 60px 20px; max-width: 480px; margin: 0 auto; }' +
      '.bopf-sp-empty-headline { font-family: Georgia, serif; font-style: italic; font-size: 22px; color: #1F1A17; margin: 0 0 16px 0; line-height: 1.4; }' +
      '.bopf-sp-empty-body { font-size: 14px; color: #666; line-height: 1.6; margin: 0 0 28px 0; }' +
      '.bopf-sp-empty-cta { display: inline-block; padding: 14px 32px; background: #1F1A17; color: #FAF6F0; ' +
        'text-decoration: none; font-size: 12px; letter-spacing: 0.18em; text-transform: uppercase; ' +
        'transition: background 0.15s; }' +
      '.bopf-sp-empty-cta:hover { background: #2c2521; }' +
      '.bopf-sp-recall-link { display: block; margin-top: 24px; font-size: 12px; color: #A0563E; ' +
        'font-style: italic; font-family: Georgia, serif; cursor: pointer; text-decoration: underline; }' +
      '.bopf-sp-recall-link:hover { color: #8a4732; }' +

      // Email recall form
      '.bopf-sp-recall-form { max-width: 380px; margin: 32px auto 0; padding: 24px; background: #FAF6F0; ' +
        'border-radius: 6px; }' +
      '.bopf-sp-recall-form label { display: block; font-size: 12px; color: #666; margin-bottom: 8px; ' +
        'letter-spacing: 0.06em; text-transform: uppercase; }' +
      '.bopf-sp-recall-form input { width: 100%; padding: 12px 14px; font-size: 15px; font-family: inherit; ' +
        'color: #1F1A17; border: 1px solid #d6cfc4; background: #fff; border-radius: 4px; box-sizing: border-box; ' +
        'margin-bottom: 12px; }' +
      '.bopf-sp-recall-form input:focus { outline: none; border-color: #A0563E; }' +
      '.bopf-sp-recall-form button { width: 100%; padding: 12px; background: #A0563E; color: #fff; border: none; ' +
        'border-radius: 4px; font-size: 13px; letter-spacing: 0.05em; cursor: pointer; transition: background 0.15s; }' +
      '.bopf-sp-recall-form button:hover { background: #8a4732; }' +
      '.bopf-sp-recall-form button:disabled { opacity: 0.5; cursor: not-allowed; }' +
      '.bopf-sp-recall-error { font-size: 13px; color: #a8443a; margin: 8px 0 0 0; min-height: 18px; }' +

      // Loading state
      '.bopf-sp-loading { text-align: center; padding: 80px 20px; }' +
      '.bopf-sp-loading-text { font-family: Georgia, serif; font-style: italic; color: #666; font-size: 15px; }' +

      // Email indicator (top-right, subtle)
      '.bopf-sp-email-indicator { position: absolute; top: 8px; right: 8px; font-size: 11px; color: #999; ' +
        'font-family: Georgia, serif; font-style: italic; }' +
      '.bopf-sp-email-indicator a { color: #A0563E; text-decoration: underline; cursor: pointer; }' +

      // Share button row (below grid)
      '.bopf-sp-share-row { text-align: center; margin: 56px auto 0; max-width: 480px; padding: 0 20px; }' +
      '.bopf-sp-share-btn { display: inline-block; padding: 12px 28px; background: transparent; ' +
        'color: #1F1A17; text-decoration: none; border: 1px solid #1F1A17; cursor: pointer; ' +
        'font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; transition: background 0.15s, color 0.15s; }' +
      '.bopf-sp-share-btn:hover { background: #1F1A17; color: #FAF6F0; }' +
      '.bopf-sp-share-hint { font-family: Georgia, serif; font-style: italic; font-size: 12px; ' +
        'color: #888; margin: 14px 0 0 0; }' +

      // Share confirm modal
      '.bopf-sp-modal-overlay { position: fixed; inset: 0; z-index: 100000; background: rgba(20,15,12,0.55); ' +
        'backdrop-filter: blur(2px); display: flex; align-items: center; justify-content: center; padding: 20px; ' +
        'animation: bopfSpFadeIn 0.18s ease; }' +
      '@keyframes bopfSpFadeIn { from { opacity: 0; } to { opacity: 1; } }' +
      '.bopf-sp-modal-card { background: #FAF6F0; border-radius: 8px; width: 100%; max-width: 420px; ' +
        'padding: 30px 28px 26px; box-shadow: 0 20px 60px rgba(20,15,12,.25); position: relative; ' +
        'font-family: -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif; color: #1F1A17; }' +
      '.bopf-sp-modal-x { position: absolute; top: 10px; right: 12px; background: none; border: none; ' +
        'font-size: 24px; line-height: 1; color: #999; cursor: pointer; padding: 6px 10px; border-radius: 50%; }' +
      '.bopf-sp-modal-x:hover { background: rgba(0,0,0,.05); color: #1F1A17; }' +
      '.bopf-sp-modal-headline { font-family: Georgia, serif; font-size: 22px; line-height: 1.25; ' +
        'margin: 0 0 12px 0; font-weight: 400; }' +
      '.bopf-sp-modal-headline em { font-style: italic; color: #A0563E; }' +
      '.bopf-sp-modal-body { font-size: 14px; line-height: 1.55; color: #3a3530; margin: 0 0 20px 0; }' +
      '.bopf-sp-modal-primary { display: block; width: 100%; padding: 13px; background: #A0563E; color: #fff; ' +
        'border: none; border-radius: 4px; font-size: 12px; letter-spacing: 0.15em; text-transform: uppercase; ' +
        'cursor: pointer; transition: background 0.15s; margin-bottom: 8px; }' +
      '.bopf-sp-modal-primary:hover { background: #8a4732; }' +
      '.bopf-sp-modal-primary:disabled { opacity: 0.6; cursor: not-allowed; }' +
      '.bopf-sp-modal-cancel { display: block; width: 100%; padding: 10px; background: transparent; ' +
        'color: #666; border: none; cursor: pointer; font-size: 13px; }' +
      '.bopf-sp-modal-cancel:hover { color: #1F1A17; }' +
      '.bopf-sp-modal-error { font-size: 13px; color: #a8443a; margin: 10px 0 0 0; min-height: 18px; }' +

      // Toast (link-copied confirmation)
      '.bopf-sp-toast { position: fixed; bottom: 30px; left: 50%; transform: translateX(-50%) translateY(20px); ' +
        'background: #1F1A17; color: #FAF6F0; padding: 14px 22px; border-radius: 4px; font-size: 13px; ' +
        'box-shadow: 0 8px 30px rgba(0,0,0,0.2); z-index: 100001; opacity: 0; transition: opacity 0.25s, transform 0.25s; ' +
        'max-width: 360px; text-align: center; }' +
      '.bopf-sp-toast.visible { opacity: 1; transform: translateX(-50%) translateY(0); }' +
      '.bopf-sp-toast--error { background: #8a4732; }' +
      '.bopf-sp-toast--with-link { padding: 18px 22px; opacity: 1; transform: translateX(-50%) translateY(0); ' +
        'min-width: 320px; text-align: left; padding-right: 50px; }' +
      '.bopf-sp-toast-title { font-family: Georgia, serif; font-style: italic; font-size: 13px; margin: 0 0 8px 0; ' +
        'color: #FAF6F0; opacity: 0.85; }' +
      '.bopf-sp-toast-input { width: 100%; padding: 8px 10px; background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2); ' +
        'color: #FAF6F0; font-size: 12px; border-radius: 3px; box-sizing: border-box; font-family: monospace; }' +
      '.bopf-sp-toast-close { position: absolute; top: 8px; right: 10px; background: none; border: none; ' +
        'color: rgba(250,246,240,0.7); font-size: 22px; line-height: 1; cursor: pointer; padding: 4px 8px; }' +
      '.bopf-sp-toast-close:hover { color: #FAF6F0; }';

    document.head.appendChild(s);
  }

  // ── Backend helpers ────────────────────────────────────────────────
  function fetchListByEmail(email) {
    return fetch(ZARA_BACKEND + '/zara/wishlist/list?email=' + encodeURIComponent(email))
      .then(function(r){ return r.json(); });
  }

  function postRemove(email, handle) {
    return fetch(ZARA_BACKEND + '/zara/wishlist/remove', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email, product_handle: handle })
    }).then(function(r){ return r.json().catch(function(){return {ok:false};}); });
  }

  // ── Renderers ──────────────────────────────────────────────────────
  function renderHeader() {
    return '' +
      '<div class="bopf-sp-header">' +
        '<p class="bopf-sp-eyebrow">A note from Hadiza</p>' +
        '<h1 class="bopf-sp-title">Your <em>saved pieces</em></h1>' +
        '<p class="bopf-sp-subtitle">An edit of pieces you\'re keeping an eye on. I\'ll let you know when something changes.</p>' +
      '</div>';
  }

  function renderEmpty(showRecallLink) {
    var html = '' +
      '<div class="bopf-sp-empty">' +
        '<p class="bopf-sp-empty-headline">You haven\'t saved anything yet.</p>' +
        '<p class="bopf-sp-empty-body">Click the heart on any piece you\'re keeping an eye on, and I\'ll keep tabs on it for you.</p>' +
        '<a href="/collections/all" class="bopf-sp-empty-cta">Browse pieces</a>';
    if (showRecallLink) {
      html += '<a class="bopf-sp-recall-link" data-bopf-show-recall>Have you saved on another device? Enter your email</a>';
      html += '<div data-bopf-recall-form-slot></div>';
    }
    html += '</div>';
    return html;
  }

  function renderRecallForm() {
    return '' +
      '<form class="bopf-sp-recall-form" data-bopf-recall-form>' +
        '<label for="bopf-sp-recall-email">Your email</label>' +
        '<input type="email" id="bopf-sp-recall-email" placeholder="your@email.com" required autocomplete="email" />' +
        '<button type="submit">Find my saved pieces</button>' +
        '<p class="bopf-sp-recall-error" data-bopf-recall-error></p>' +
      '</form>';
  }

  function renderEmailIndicator(email) {
    return '<div class="bopf-sp-email-indicator">' +
      'Showing pieces saved by ' + escapeHtml(email) +
      ' &middot; <a data-bopf-change-email>change</a>' +
    '</div>';
  }

  function renderCard(item) {
    var displayTitle = (item.live && item.live.title) || item.stored_title || 'Untitled';
    var displayImage = (item.live && item.live.image) || item.stored_image || '';
    var displayUrl = '/products/' + item.product_handle;
    var available = item.still_available;
    var livePrice = item.live ? item.live.price_aed : null;
    var storedPrice = item.stored_price_aed;
    var priceChanged = item.price_changed;

    // Price display logic:
    // - If live unavailable: show stored price, "Sold" overlay
    // - If price unchanged: show live price
    // - If price increased: show live price (don't draw attention)
    // - If price decreased: show strikethrough stored + live in terracotta
    var priceHtml = '';
    if (livePrice !== null) {
      if (priceChanged && storedPrice !== null && livePrice < storedPrice) {
        priceHtml = '<span class="bopf-sp-price-strike">' + fmtAed(storedPrice) + '</span>' + fmtAed(livePrice);
      } else {
        priceHtml = fmtAed(livePrice);
      }
    } else if (storedPrice !== null) {
      priceHtml = fmtAed(storedPrice);
    }

    var savedAtHtml = '';
    if (item.saved_at) {
      var d = new Date(item.saved_at);
      if (!isNaN(d.getTime())) {
        var days = Math.floor((Date.now() - d.getTime()) / 86400000);
        var savedLabel = '';
        if (days === 0) savedLabel = 'Saved today';
        else if (days === 1) savedLabel = 'Saved yesterday';
        else if (days < 7) savedLabel = 'Saved ' + days + ' days ago';
        else if (days < 30) savedLabel = 'Saved ' + Math.floor(days / 7) + ' week' + (days < 14 ? '' : 's') + ' ago';
        else savedLabel = 'Saved ' + Math.floor(days / 30) + ' month' + (days < 60 ? '' : 's') + ' ago';
        savedAtHtml = '<p class="bopf-sp-saved-at">' + savedLabel + '</p>';
      }
    }

    var waText = encodeURIComponent('Hi Hadiza! I\'m interested in ' + displayTitle + ' (' + window.location.origin + displayUrl + '). Could you tell me more about it?');
    var waUrl = 'https://wa.me/' + WHATSAPP + '?text=' + waText;

    return '' +
      '<div class="bopf-sp-card" data-handle="' + escapeHtml(item.product_handle) + '">' +
        '<div class="bopf-sp-img-wrap">' +
          '<a href="' + escapeHtml(displayUrl) + '">' +
            (displayImage ? '<img src="' + escapeHtml(displayImage) + '" alt="' + escapeHtml(displayTitle) + '" loading="lazy" />' : '') +
          '</a>' +
          '<button class="bopf-sp-remove" aria-label="Remove from saved" data-bopf-remove="' + escapeHtml(item.product_handle) + '">&times;</button>' +
          (available ? '' : '<div class="bopf-sp-sold-badge"><p class="label">No longer here</p><p class="sub">Tap × to remove</p></div>') +
        '</div>' +
        '<div class="bopf-sp-info">' +
          '<a href="' + escapeHtml(displayUrl) + '">' +
            '<p class="bopf-sp-name">' + escapeHtml(displayTitle) + '</p>' +
          '</a>' +
          (priceHtml ? '<p class="bopf-sp-price">' + priceHtml + '</p>' : '') +
          (priceChanged && storedPrice && livePrice && livePrice < storedPrice ?
            '<p class="bopf-sp-price-changed">Price came down since you saved</p>' : '') +
          savedAtHtml +
          '<a href="' + escapeHtml(waUrl) + '" target="_blank" rel="noopener" class="bopf-sp-wa-link">Ask Hadiza about this piece</a>' +
        '</div>' +
      '</div>';
  }

  function renderGrid(items, email) {
    if (!items.length) return renderEmpty(true);
    var emailIndicator = email ? renderEmailIndicator(email) : '';
    return emailIndicator +
      '<div class="bopf-sp-grid">' +
        items.map(renderCard).join('') +
      '</div>';
  }

  // ── Action wiring ──────────────────────────────────────────────────
  function wireActions(root) {
    // Remove buttons
    root.querySelectorAll('[data-bopf-remove]').forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        var handle = btn.getAttribute('data-bopf-remove');
        var card = btn.closest('.bopf-sp-card');
        if (!card) return;
        // Optimistic UI: hide the card immediately, even before backend confirms.
        card.style.transition = 'opacity 0.2s, transform 0.2s';
        card.style.opacity = '0';
        card.style.transform = 'scale(0.95)';
        setTimeout(function(){
          if (card.parentNode) card.parentNode.removeChild(card);
          // If grid is now empty, re-render the empty state.
          var grid = root.querySelector('.bopf-sp-grid');
          if (grid && grid.children.length === 0) {
            var email = getEmail();
            grid.outerHTML = renderEmpty(true);
            wireActions(root);
          }
        }, 220);
        // Update local state.
        markUnsavedLocal(handle);
        // Hit backend (non-blocking).
        var email = getEmail();
        if (email) {
          postRemove(email, handle).then(function(d){ log('remove backend:', d); });
        }
      });
    });

    // Email change link
    var changeBtn = root.querySelector('[data-bopf-change-email]');
    if (changeBtn) {
      changeBtn.addEventListener('click', function(e) {
        e.preventDefault();
        if (!confirm('Sign out and clear your saved pieces from this device? You can recall them again with your email.')) return;
        try { localStorage.removeItem(STORAGE_EMAIL); localStorage.removeItem(STORAGE_SAVED); } catch(e) {}
        boot();
      });
    }

    // Show-recall link in empty state
    var showRecallBtn = root.querySelector('[data-bopf-show-recall]');
    if (showRecallBtn) {
      showRecallBtn.addEventListener('click', function(e) {
        e.preventDefault();
        var slot = root.querySelector('[data-bopf-recall-form-slot]');
        if (slot) {
          slot.innerHTML = renderRecallForm();
          wireRecallForm(root);
          var input = slot.querySelector('input');
          if (input) setTimeout(function(){ input.focus(); }, 50);
        }
        showRecallBtn.style.display = 'none';
      });
    }
  }

  function wireRecallForm(root) {
    var form = root.querySelector('[data-bopf-recall-form]');
    if (!form) return;
    form.addEventListener('submit', function(e) {
      e.preventDefault();
      var input = form.querySelector('input[type="email"]');
      var errEl = form.querySelector('[data-bopf-recall-error]');
      var btn = form.querySelector('button');
      var email = (input.value || '').trim().toLowerCase();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        errEl.textContent = 'Please enter a valid email.';
        return;
      }
      errEl.textContent = '';
      btn.disabled = true;
      btn.textContent = 'Looking up...';
      fetchListByEmail(email).then(function(d) {
        if (d.ok) {
          setEmail(email);
          // Cache items locally for fast re-paint next time.
          if (d.items && d.items.length) {
            var saved = {};
            d.items.forEach(function(it){ saved[it.product_handle] = it.saved_at ? new Date(it.saved_at).getTime() : Date.now(); });
            setSavedMap(saved);
          }
          renderApp(d.items, email);
        } else {
          errEl.textContent = d.error || 'Could not retrieve your saved pieces.';
          btn.disabled = false;
          btn.textContent = 'Find my saved pieces';
        }
      }).catch(function(err) {
        log('recall error:', err);
        errEl.textContent = 'Connection error. Please try again.';
        btn.disabled = false;
        btn.textContent = 'Find my saved pieces';
      });
    });
  }

  // ── Share button ───────────────────────────────────────────────────
  function wireShareButton(root, email) {
    var btn = root.querySelector('[data-bopf-share]');
    if (!btn) return;
    btn.addEventListener('click', function(e) {
      e.preventDefault();
      if (!email) {
        showShareToast('We need your email to create a share link. Try saving a piece first.', true);
        return;
      }
      openShareConfirmModal(email);
    });
  }

  function openShareConfirmModal(email) {
    // Remove any existing modal first.
    var existing = document.getElementById('bopf-share-modal');
    if (existing) existing.remove();

    var modal = document.createElement('div');
    modal.id = 'bopf-share-modal';
    modal.className = 'bopf-sp-modal-overlay';
    modal.innerHTML = '' +
      '<div class="bopf-sp-modal-card">' +
        '<button class="bopf-sp-modal-x" aria-label="Close">&times;</button>' +
        '<p class="bopf-sp-eyebrow">A shareable link</p>' +
        '<h2 class="bopf-sp-modal-headline">Share your <em>edit</em></h2>' +
        '<p class="bopf-sp-modal-body">' +
          'I\'ll generate a private link to your saved pieces. Anyone with the link can view your edit for 30 days.' +
          ' Your email won\'t be visible.' +
        '</p>' +
        '<button class="bopf-sp-modal-primary" data-bopf-share-confirm>Create my link</button>' +
        '<button class="bopf-sp-modal-cancel" data-bopf-share-cancel>Not now</button>' +
        '<p class="bopf-sp-modal-error" data-bopf-share-error></p>' +
      '</div>';
    document.body.appendChild(modal);

    // Focus management
    setTimeout(function(){
      var primary = modal.querySelector('.bopf-sp-modal-primary');
      if (primary) primary.focus();
    }, 50);

    function close() { if (modal.parentNode) modal.parentNode.removeChild(modal); }

    modal.querySelector('.bopf-sp-modal-x').addEventListener('click', close);
    modal.querySelector('[data-bopf-share-cancel]').addEventListener('click', close);
    modal.addEventListener('click', function(e) {
      if (e.target === modal) close();
    });
    document.addEventListener('keydown', function escHandler(e) {
      if (e.key === 'Escape') {
        close();
        document.removeEventListener('keydown', escHandler);
      }
    });

    var confirmBtn = modal.querySelector('[data-bopf-share-confirm]');
    var errEl = modal.querySelector('[data-bopf-share-error]');
    confirmBtn.addEventListener('click', function() {
      confirmBtn.disabled = true;
      confirmBtn.textContent = 'Creating link...';
      errEl.textContent = '';
      postCreateShare(email).then(function(d) {
        if (!d.ok) {
          errEl.textContent = d.error || 'Could not create link. Please try again.';
          confirmBtn.disabled = false;
          confirmBtn.textContent = 'Create my link';
          return;
        }
        close();
        deliverShareLink(d.url);
      }).catch(function(err) {
        log('share create error:', err);
        errEl.textContent = 'Connection error. Please try again.';
        confirmBtn.disabled = false;
        confirmBtn.textContent = 'Create my link';
      });
    });
  }

  function deliverShareLink(url) {
    // On mobile, prefer the native share sheet so the customer can pick
    // WhatsApp, Messages, etc. directly. On desktop, fall back to copy
    // to clipboard with a confirmation toast.
    var canNativeShare = !!(navigator.share && navigator.canShare && navigator.canShare({ url: url }));
    if (canNativeShare) {
      navigator.share({
        title: 'My BOPF saved pieces',
        text: 'A few pieces I\'m keeping an eye on at BOPF',
        url: url
      }).then(function() {
        showShareToast('Link shared.');
      }).catch(function() {
        // User cancelled the share sheet. Fall back to clipboard.
        copyToClipboard(url);
      });
    } else {
      copyToClipboard(url);
    }
  }

  function copyToClipboard(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(function() {
        showShareToast('Link copied. Paste it anywhere to share.');
      }).catch(function() {
        showShareToastWithLink(text);
      });
    } else {
      showShareToastWithLink(text);
    }
  }

  // Fallback when clipboard API isn\'t available: show the link in a
  // toast with manual-select instructions.
  function showShareToastWithLink(url) {
    var existing = document.getElementById('bopf-share-toast');
    if (existing) existing.remove();
    var toast = document.createElement('div');
    toast.id = 'bopf-share-toast';
    toast.className = 'bopf-sp-toast bopf-sp-toast--with-link';
    toast.innerHTML = '' +
      '<p class="bopf-sp-toast-title">Your share link</p>' +
      '<input type="text" readonly value="' + escapeHtml(url) + '" class="bopf-sp-toast-input" />' +
      '<button class="bopf-sp-toast-close" aria-label="Close">&times;</button>';
    document.body.appendChild(toast);
    var input = toast.querySelector('input');
    setTimeout(function(){ input.focus(); input.select(); }, 50);
    toast.querySelector('.bopf-sp-toast-close').addEventListener('click', function(){
      toast.remove();
    });
    setTimeout(function(){ if (toast.parentNode) toast.remove(); }, 12000);
  }

  function showShareToast(message, isError) {
    var existing = document.getElementById('bopf-share-toast');
    if (existing) existing.remove();
    var toast = document.createElement('div');
    toast.id = 'bopf-share-toast';
    toast.className = 'bopf-sp-toast' + (isError ? ' bopf-sp-toast--error' : '');
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(function(){ toast.classList.add('visible'); }, 10);
    setTimeout(function(){
      toast.classList.remove('visible');
      setTimeout(function(){ if (toast.parentNode) toast.remove(); }, 300);
    }, 4500);
  }

  // ── App orchestrator ───────────────────────────────────────────────
  function renderApp(items, email) {
    var root = document.getElementById('bopf-saved-app');
    if (!root) return;
    var shareButtonHtml = (items && items.length) ? renderShareButton() : '';
    root.innerHTML = renderHeader() + renderGrid(items || [], email) + shareButtonHtml;
    wireActions(root);
    wireShareButton(root, email);
  }

  function renderShareButton() {
    return '' +
      '<div class="bopf-sp-share-row">' +
        '<a class="bopf-sp-share-btn" data-bopf-share>Share my saved pieces</a>' +
        '<p class="bopf-sp-share-hint">Send a 30-day link to a friend. Your email stays private.</p>' +
      '</div>';
  }

  // Renders the shared-view variant of the page. Read-only, no remove
  // buttons, no email indicator, no share button. Anonymous header so
  // no PII leaks to the viewer.
  function renderSharedApp(items, expiresAt) {
    var root = document.getElementById('bopf-saved-app');
    if (!root) return;
    var headerHtml = '' +
      '<div class="bopf-sp-header">' +
        '<p class="bopf-sp-eyebrow">A shared edit</p>' +
        '<h1 class="bopf-sp-title">A friend\'s <em>saved pieces</em></h1>' +
        '<p class="bopf-sp-subtitle">Curated picks from a BOPF customer. Tap any piece to see it on our storefront.</p>' +
      '</div>';
    if (!items.length) {
      root.innerHTML = headerHtml +
        '<div class="bopf-sp-empty">' +
          '<p class="bopf-sp-empty-headline">This wishlist is empty.</p>' +
          '<a href="/collections/all" class="bopf-sp-empty-cta">Browse pieces</a>' +
        '</div>';
      return;
    }
    var grid = '<div class="bopf-sp-grid">' +
      items.map(function(it){ return renderSharedCard(it); }).join('') +
    '</div>';
    var ctaHtml = '' +
      '<div class="bopf-sp-share-row">' +
        '<a href="/collections/all" class="bopf-sp-share-btn">Discover your own pieces</a>' +
        '<p class="bopf-sp-share-hint">Like what you see? Build your own edit.</p>' +
      '</div>';
    root.innerHTML = headerHtml + grid + ctaHtml;
  }

  // A simpler card variant for shared view: no remove button, no
  // "Ask Hadiza about this piece" link (would expose context that the
  // sharer might not want surfaced to a viewer).
  function renderSharedCard(item) {
    var displayTitle = (item.live && item.live.title) || item.stored_title || 'Untitled';
    var displayImage = (item.live && item.live.image) || item.stored_image || '';
    var displayUrl = '/products/' + item.product_handle;
    var available = item.still_available;
    var livePrice = item.live ? item.live.price_aed : null;
    var priceHtml = livePrice !== null ? fmtAed(livePrice) : '';

    return '' +
      '<div class="bopf-sp-card">' +
        '<div class="bopf-sp-img-wrap">' +
          '<a href="' + escapeHtml(displayUrl) + '">' +
            (displayImage ? '<img src="' + escapeHtml(displayImage) + '" alt="' + escapeHtml(displayTitle) + '" loading="lazy" />' : '') +
          '</a>' +
          (available ? '' : '<div class="bopf-sp-sold-badge"><p class="label">No longer here</p></div>') +
        '</div>' +
        '<div class="bopf-sp-info">' +
          '<a href="' + escapeHtml(displayUrl) + '">' +
            '<p class="bopf-sp-name">' + escapeHtml(displayTitle) + '</p>' +
          '</a>' +
          (priceHtml ? '<p class="bopf-sp-price">' + priceHtml + '</p>' : '') +
        '</div>' +
      '</div>';
  }

  function renderShareError(reason, statusCode) {
    var root = document.getElementById('bopf-saved-app');
    if (!root) return;
    var headline, body;
    if (reason === 'expired') {
      headline = 'This share link has expired.';
      body = 'Share links last 30 days. Ask the sender for a fresh one, or browse our latest pieces.';
    } else if (reason === 'revoked') {
      headline = 'This share link has been revoked.';
      body = 'The owner of this wishlist has chosen to stop sharing it.';
    } else if (reason === 'connection') {
      headline = 'Connection trouble.';
      body = 'We couldn\'t load this shared wishlist. Please try again in a moment.';
    } else {
      headline = 'This share link isn\'t valid.';
      body = 'The link may be mistyped, or it may belong to a wishlist that no longer exists.';
    }
    root.innerHTML = '' +
      '<div class="bopf-sp-header">' +
        '<p class="bopf-sp-eyebrow">A shared edit</p>' +
        '<h1 class="bopf-sp-title">Saved <em>pieces</em></h1>' +
      '</div>' +
      '<div class="bopf-sp-empty">' +
        '<p class="bopf-sp-empty-headline">' + escapeHtml(headline) + '</p>' +
        '<p class="bopf-sp-empty-body">' + escapeHtml(body) + '</p>' +
        '<a href="/collections/all" class="bopf-sp-empty-cta">Browse pieces</a>' +
      '</div>';
  }

  function renderLoading(reason) {
    var root = document.getElementById('bopf-saved-app');
    if (!root) return;
    root.innerHTML = renderHeader() +
      '<div class="bopf-sp-loading">' +
        '<p class="bopf-sp-loading-text">' + escapeHtml(reason || 'Just a moment...') + '</p>' +
      '</div>';
  }

  // Read ?share=TOKEN from the URL. Returns the token or null.
  function getShareToken() {
    try {
      var u = new URL(window.location.href);
      var t = (u.searchParams.get('share') || '').trim();
      return /^[a-z0-9]{16,32}$/.test(t) ? t : null;
    } catch(e) { return null; }
  }

  function fetchSharedList(token) {
    return fetch(ZARA_BACKEND + '/zara/wishlist/share/view?token=' + encodeURIComponent(token))
      .then(function(r){ return r.json().then(function(d){ d._status = r.status; return d; }); });
  }

  function postCreateShare(email) {
    return fetch(ZARA_BACKEND + '/zara/wishlist/share/create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email })
    }).then(function(r){ return r.json(); });
  }

  function boot() {
    injectStyles();
    var root = document.getElementById('bopf-saved-app');
    if (!root) {
      log('no #bopf-saved-app element found on page');
      return;
    }

    // Share-view mode: URL has ?share=TOKEN. We render a read-only view
    // of someone else's wishlist. No remove buttons, no email indicator,
    // no share button. The owner's email is never disclosed.
    var shareToken = getShareToken();
    if (shareToken) {
      log('share-view mode, token=' + shareToken);
      renderLoading('Loading shared wishlist...');
      fetchSharedList(shareToken).then(function(d) {
        if (d.ok) {
          renderSharedApp(d.items || [], d.expires_at);
        } else {
          renderShareError(d.error || 'invalid', d._status);
        }
      }).catch(function(err) {
        log('share fetch failed:', err);
        renderShareError('connection');
      });
      return;
    }

    var email = getEmail();
    var savedMap = getSavedMap();
    var hasLocalItems = Object.keys(savedMap).length > 0;

    // Case 1: no email and no local items -> empty state with recall option
    if (!email && !hasLocalItems) {
      log('no email, no local items');
      root.innerHTML = renderHeader() + renderEmpty(true);
      wireActions(root);
      return;
    }

    // Case 2: have email -> fetch from backend (authoritative)
    if (email) {
      log('fetching for', email);
      renderLoading('Loading your saved pieces...');
      fetchListByEmail(email).then(function(d) {
        if (d.ok) {
          // Cache the fresh list for next visit.
          if (d.items) {
            var saved = {};
            d.items.forEach(function(it){
              saved[it.product_handle] = it.saved_at ? new Date(it.saved_at).getTime() : Date.now();
            });
            setSavedMap(saved);
          }
          renderApp(d.items || [], email);
        } else {
          log('list error:', d.error);
          renderApp([], email);
        }
      }).catch(function(err) {
        log('fetch failed:', err);
        renderApp([], email);
      });
      return;
    }

    // Case 3: have local items but no email - this is unusual (the heart
    // flow always captures email) but possible if the customer declined
    // the popup. Show a friendly recall prompt to associate their device
    // with an email so they can retrieve cross-device.
    log('have local items but no email');
    root.innerHTML = renderHeader() +
      '<div class="bopf-sp-empty">' +
        '<p class="bopf-sp-empty-headline">Your saved pieces live on this device.</p>' +
        '<p class="bopf-sp-empty-body">Add your email so I can keep them safe across devices and let you know when prices change.</p>' +
        renderRecallForm() +
      '</div>';
    wireRecallForm(root);
  }

  // ── Init ───────────────────────────────────────────────────────────
  log('script parsed, readyState =', document.readyState);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();